import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { EditableInputRenderer } from '@ubeya/shared-web/components';
import { isPayrollFieldEditableOnTimesheet } from 'shared/utils/timesheet';
import { PAYROLL_FIELDS_WITHOUT_CURRENCY } from 'payroll/constants';

const TimesheetPayrollFieldRenderer = ({ rowData, columnData: { fieldId, updateField } }) => {
  const { timesheetPayrollFields } = rowData || {};
  const { currency } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const field = useMemo(
    () => (timesheetPayrollFields?.fields || []).find((payrollField) => payrollField?.id === fieldId),
    [fieldId, timesheetPayrollFields?.fields]
  );
  const fieldValue = formattingMap.number(field?.value ?? 0);

  const [value, setValue] = useState(fieldValue);
  const [prevValue, setPrevValue] = useState(fieldValue);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValue(fieldValue);
    setPrevValue(fieldValue);
  }, [fieldValue]);

  const updateValue = useCallback(async () => {
    if (prevValue === value) {
      return;
    }

    setIsSaving(true);
    try {
      await updateField({
        employeeSlotId: timesheetPayrollFields?.employeeSlotId,
        jobId: timesheetPayrollFields?.jobId,
        payrollFieldId: fieldId,
        value: value || 0
      });
      setPrevValue(value);
    } catch (error) {
      setValue(prevValue);
    } finally {
      setIsSaving(false);
    }
  }, [prevValue, value, updateField, timesheetPayrollFields?.employeeSlotId, timesheetPayrollFields?.jobId, fieldId]);

  if (!timesheetPayrollFields) {
    return <></>;
  }

  return (
    <EditableInputRenderer
      isLoading={isSaving}
      value={value}
      onChange={setValue}
      onSubmitChange={updateValue}
      prefix={PAYROLL_FIELDS_WITHOUT_CURRENCY.includes(fieldId) ? '' : currency}
      readOnly={!isPayrollFieldEditableOnTimesheet(rowData)}
      fillWidth
    />
  );
};

export default TimesheetPayrollFieldRenderer;
